import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useSetAtom } from 'jotai';
import { useMemo, type FC, type ReactNode, useEffect } from 'react';

import type { Country } from '../entities/Country';
import type { Environment, StripeBetaKey } from '../entities/enums';
import {
  getStripeBetaPublicKey,
  getStripePublicKey,
} from '../utils/getStripePublicKey';

import { isStripeLoadedAtom } from './StripeProviderStore';

const StripeProvider: FC<{
  children: ReactNode;
  advertiserNation: Country;
  env: Environment;
  stripeBetaKey?: StripeBetaKey; // optional parameter use as indicator as to lookup Stripe beta key. To tackle scenario of one country have two Stripe accounts.
}> = ({ children, advertiserNation, env, stripeBetaKey }) => {
  const stripePublicKey = stripeBetaKey
    ? getStripeBetaPublicKey(stripeBetaKey as StripeBetaKey, env) // retrieve Stripe beta key when param "stripeBetaKey" is not null
    : getStripePublicKey(advertiserNation, env);

  const betas: string[] = [];
  if (stripeBetaKey) {
    betas.push(stripeBetaKey);
  }

  const stripePromise = useMemo(
    () => loadStripe(stripePublicKey, { betas }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stripePublicKey],
  );

  return (
    // what is options used for?
    // <Elements stripe={stripePromise} key={stripePublicKey}  options={{ locale: 'en' }}>
    <Elements stripe={stripePromise} key={stripePublicKey}>
      <CheckIsStripeLoaded>
        <>{children}</>
      </CheckIsStripeLoaded>
    </Elements>
  );
};

const CheckIsStripeLoaded = ({ children }: { children: ReactNode }) => {
  const stripe = useStripe();
  const elements = useElements();
  const setIsStripeLoaded = useSetAtom(isStripeLoadedAtom);

  useEffect(() => {
    if (stripe === null || elements === null) {
      setIsStripeLoaded(false);
    } else {
      setIsStripeLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe]);

  return <>{children}</>;
};

export { StripeProvider };
